import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _find from 'lodash/find';

export default class RenderDropdownSelect extends Component {
  constructor(props) {
    super(props);

    this.onWindowClick = this.onWindowClick.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.updateField = this.updateField.bind(this);
    this.setActiveField = this.setActiveField.bind(this);
    this.findLabel = this.findLabel.bind(this);
    this.hide = this.hide.bind(this);

    this.nodeRef = React.createRef();

    const { value } = props.input;
    this.state = {
      isOpen: false,
      activeField: value,
      touchedOverride: false,
      isClosed: false,
    };
  }

  componentDidMount() {
    window.addEventListener('click', this.onWindowClick);
    window.addEventListener('touchstart', this.onWindowClick);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.onWindowClick);
    window.removeEventListener('touchstart', this.onWindowClick);
  }

  onWindowClick(e) {
    const { isOpen } = this.state;
    const dropdownElement = this.nodeRef.current;
    if (e.target !== dropdownElement && !dropdownElement.contains(e.target) && isOpen) {
      this.hide();
    }
  }

  setActiveField(activeField) {
    this.setState({
      activeField,
    });
  }

  updateField(value) {
    const { input, handleOnSelect } = this.props;
    input.onChange(value);
    if (handleOnSelect) {
      handleOnSelect(value);
    }
    this.setActiveField(value);
    this.setState({ touchedOverride: true });
  }

  toggleDropdown() {
    const { input, handleToggleDropdown } = this.props;
    const { value } = input.value;
    const activeField = value;
    if (handleToggleDropdown) {
      handleToggleDropdown(input.name);
    }
    this.setState(
      (state) => ({
        isOpen: !state.isOpen,
        activeField,
      }),
      () => {
        const { isOpen } = this.state;
        if (!isOpen) {
          this.setState({
            isClosed: true,
          });
        } else {
          this.setState({
            isClosed: false,
          });
        }
        const activeEl = document.getElementById(activeField);
        if (activeEl) {
          this.selectDropdown.scrollTop = activeEl.offsetTop - 80;
        }
      }
    );
  }

  findLabel(value) {
    const { options } = this.props;
    const selectedOption = _find(options, { value: value }); // eslint-disable-line
    if (selectedOption) {
      return selectedOption.title;
    }
    return null;
  }

  hide() {
    this.setState({
      isOpen: false,
      isClosed: true,
    });
    const { handleToggleDropdown, input } = this.props;
    if (handleToggleDropdown) {
      handleToggleDropdown(input.name);
    }
  }

  render() {
    const {
      cb,
      options,
      input,
      label,
      id,
      required,
      hideFormGroup,
      hideEmpty,
      selectText,
      maxHeight,
      tooltip,
      meta: { touched, error },
      placeholder,
      disabled,
      showToggleAnimation,
    } = this.props;
    const { isOpen, isClosed, activeField, touchedOverride } = this.state;

    return (
      <div
        className={`${hideFormGroup ? '' : 'form-group'} ${
          (touched || touchedOverride) && error ? 'has-error' : ''
        }`}
        ref={this.nodeRef}
      >
        {label && (
          <label htmlFor={id}>
            <span>
              {label} {required && <span className="text-danger danger-asterisk">*</span>}
            </span>
          </label>
        )}
        <div
          id={id}
          className={`scroll-select ${(touched || touchedOverride) && error ? 'has-error' : ''} ${
            tooltip ? 'with-tooltip' : ''
          } ${isOpen ? 'scroll-select-active' : ''} ${disabled ? 'scroll-select-disabled' : ''}`}
          data-tooltip={tooltip}
          onClick={() => {
            if (!disabled) {
              this.toggleDropdown();
            }
          }}
        >
          <div className="btn btn-dropdown btn-boxed">
            {input.value === '' ? (
              <span className="scroll-value">{placeholder || 'Select...'}</span>
            ) : (
              <span className={`scroll-value ${input.value ? 'scroll-value-chosen' : ''}`}>
                {this.findLabel(input.value) || placeholder || 'Select...'}
              </span>
            )}
            {showToggleAnimation && (
              <span
                className={classNames(
                  { 'zolaicon-chevron-closed': isClosed },
                  { 'zolaicon-chevron-open': isOpen }
                )}
              >
                <i className="zolaicon zolaicon-chevron-down" />
              </span>
            )}
            {!showToggleAnimation && (
              <i
                className={classNames(
                  'zolaicon',
                  { 'zolaicon-chevron-up': isOpen },
                  { 'zolaicon-chevron-down': !isOpen }
                )}
              />
            )}
          </div>
          <div
            className="scroll-select-dropdown"
            style={{
              display: isOpen ? 'block' : 'none',
              maxHeight: maxHeight ? `${maxHeight}px` : '',
            }}
            ref={(selectDropdown) => {
              this.selectDropdown = selectDropdown;
            }}
          >
            {!hideEmpty && (
              <div
                id={`${input.name}-empty`}
                className={`scroll-select-option scroll-select-empty ${
                  activeField === `${input.name}-empty` ? 'scroll-select-active' : ''
                }`}
                onClick={() => this.updateField('')}
                onMouseEnter={() => this.setActiveField(`${input.name}-empty`)}
              >
                {selectText || 'Select...'}
              </div>
            )}
            {options.map((opt) => (
              <div
                id={opt.value}
                key={opt.value}
                className={`scroll-select-option ${
                  activeField === opt.value ? 'scroll-select-active' : ''
                }`}
                onClick={() => {
                  this.updateField(opt.value);
                  if (cb) cb(opt.value);
                }}
                onMouseEnter={() => this.setActiveField(opt.value)}
              >
                {opt.title}
              </div>
            ))}
          </div>
        </div>
        {(touched || touchedOverride) && error && <span className="help-block">{error}</span>}
      </div>
    );
  }
}

RenderDropdownSelect.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
  }),
  options: PropTypes.arrayOf(PropTypes.shape({})),
  label: PropTypes.string,
  id: PropTypes.string,
  required: PropTypes.bool,
  hideFormGroup: PropTypes.bool,
  hideEmpty: PropTypes.bool,
  selectText: PropTypes.string,
  maxHeight: PropTypes.number,
  tooltip: PropTypes.string,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  placeholder: PropTypes.string,
  cb: PropTypes.func,
  disabled: PropTypes.bool,
  handleToggleDropdown: PropTypes.func,
  handleOnSelect: PropTypes.func,
  showToggleAnimation: PropTypes.bool,
};
